import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, HelpCircle } from 'lucide-react';
import { adminQuizzesService } from '@/services/admin-quizzes.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { QuizView } from '@/types/api';

/**
 * QuizSidebarLink — small card that renders inside the admin book /
 * article editors and summarizes the quiz attached to that parent.
 *
 * States:
 *   - No quiz yet → "Create quiz" primary-feeling CTA.
 *   - Quiz pending → dim indicator + "Edit quiz" link.
 *   - Quiz live → green check + "Edit quiz" link.
 *
 * The component makes its own fetch so callers don't need to thread the
 * quiz shape through their editor state. Silences on 404 because a
 * missing quiz is the expected null case, not an error.
 */
export interface QuizSidebarLinkProps {
  parentKind: 'book' | 'article';
  parentId: string;
  /** Disable fetching — used during the parent's create flow, when the
   *  id isn't committed yet. */
  disabled?: boolean;
  className?: string;
}

export function QuizSidebarLink({
  parentKind,
  parentId,
  disabled,
  className,
}: QuizSidebarLinkProps) {
  const [quiz, setQuiz] = useState<QuizView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (disabled || !parentId) {
      setQuiz(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const row =
          parentKind === 'book'
            ? await adminQuizzesService.getForBook(parentId)
            : await adminQuizzesService.getForArticle(parentId);
        if (!cancelled) setQuiz(row);
      } catch (err) {
        if (cancelled) return;
        // 404 here is a real miss — the endpoint returns `null` for no
        // quiz, so any thrown ApiError is unexpected. Swallow quietly
        // to avoid blocking the parent editor.
        if (!(err instanceof ApiError)) {
          console.warn('Quiz fetch failed:', err);
        }
        setQuiz(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [disabled, parentKind, parentId]);

  const hasQuiz = !!quiz;
  const isLive = quiz?.status === 'live';

  const to = hasQuiz
    ? `/admin/quizzes/${quiz!.id}`
    : parentKind === 'book'
      ? `/admin/quizzes/new?bookId=${encodeURIComponent(parentId)}`
      : `/admin/quizzes/new?articleId=${encodeURIComponent(parentId)}`;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
        Quiz
      </div>
      {loading ? (
        <div className="rounded-lg border border-dashed border-or-slate-200 bg-white/60 px-3 py-2 text-[12px] text-or-slate-500">
          Checking…
        </div>
      ) : hasQuiz ? (
        <Link
          to={to}
          className="flex items-center justify-between gap-2 rounded-lg border border-or-slate-200 bg-white px-3 py-2 text-[13px] text-or-slate-800 hover:bg-or-slate-50"
        >
          <span className="inline-flex items-center gap-1.5">
            {isLive ? (
              <CheckCircle2 className="h-4 w-4 text-or-green-700" aria-hidden />
            ) : (
              <HelpCircle className="h-4 w-4 text-or-yellow-400" aria-hidden />
            )}
            <span className="font-semibold">{isLive ? 'Live' : 'Pending'}</span>
            <span className="text-or-slate-500">· Edit</span>
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-or-slate-400" aria-hidden />
        </Link>
      ) : (
        <Link
          to={to}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold',
            'border border-or-orange-200 bg-or-orange-50 text-or-orange-700 hover:bg-or-orange-100',
          )}
        >
          <HelpCircle className="h-4 w-4" aria-hidden />
          Create quiz
        </Link>
      )}
      {!hasQuiz && !loading ? (
        <p className="text-[11px] text-or-slate-500">
          Authors can add a 5-question quiz to reinforce the core ideas.
        </p>
      ) : null}
    </div>
  );
}
